import type { Tagged } from "@-/com.npmjs.type-fest";
import { createGate } from "@-/dev.improve.haniverse.lib.gate";
import { useHasHydrated } from "@-/dev.improve.haniverse.lib.hydration";
import { isRozeniteNativeDevtoolsAvailable } from "#is-rozenite-native-devtools-available";

export type RootLayoutNavigationDevtoolsGate = Tagged<
  Record<never, never>,
  "RootLayoutNavigationDevtoolsGate"
>;

/**
 * Whether the navigation devtools mount: hydrated, and Fusebox devtools are attached. `null` is the
 * designed hidden state.
 *
 * `isRozeniteNativeDevtoolsAvailable()` reads `globalThis.__FUSEBOX_REACT_DEVTOOLS_DISPATCHER__`,
 * which exists only in a browser. Evaluating it during the server render and the hydrating client
 * render made the server emit NOTHING here while the client emitted the whole subtree
 * (`RozeniteOverlay` + its host `View`s), so React hit "server rendered HTML didn't match the
 * client" at the enclosing `PortalProvider`, discarded the ENTIRE server tree and re-rendered from
 * scratch — throwing away the server-rendered CSS and markup, i.e. the flash of unstyled content.
 * Waiting for hydration keeps the hydrating render identical to the server's. Nothing is lost: this
 * is a dev-only devtools overlay.
 */
export function useRootLayoutNavigationDevtoolsGate(): RootLayoutNavigationDevtoolsGate | null {
  const hasHydrated = useHasHydrated();
  return hasHydrated && isRozeniteNativeDevtoolsAvailable()
    ? createGate<RootLayoutNavigationDevtoolsGate>({})
    : null;
}
